import React, { useState, useEffect } from "react";
import { Modal, Button, Switch, Form, Row, Col, Typography, Space } from "antd";
import { Upload as UploadIcon, Image, FileText, Receipt } from "lucide-react";
import toast from "react-hot-toast";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import ElementoImagem from "./ElementoImagem";

const { Title, Text } = Typography;

function ModalConfiguracoesCabecalhoRodape({
  openModal,
  handleModalClose,
  modalData,
  handleUpdateCabecalhoRodape,
  sendRequest,
  clinicaId,
  auth,
  dadosClinica,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [configuracao, setConfiguracao] = useState({
    orcamentoParticular:
      dadosClinica.cabecalhoRodape?.orcamentoParticular || false,
    orcamentoSegurado: dadosClinica.cabecalhoRodape?.orcamentoSegurado || false,
    planoTratamento: dadosClinica.cabecalhoRodape?.planoTratamento || false,
    atestado: dadosClinica.cabecalhoRodape?.atestado || false,
  });

  const [imagens, setImagens] = useState([]);

  useEffect(() => {
    if (dadosClinica) {
      setConfiguracao({
        orcamentoParticular:
          dadosClinica.cabecalhoRodape?.orcamentoParticular || false,
        orcamentoSegurado:
          dadosClinica.cabecalhoRodape?.orcamentoSegurado || false,
        planoTratamento: dadosClinica.cabecalhoRodape?.planoTratamento || false,
        atestado: dadosClinica.cabecalhoRodape?.atestado || false,
      });

      form.setFieldsValue({
        orcamentoParticular:
          dadosClinica.cabecalhoRodape?.orcamentoParticular || false,
        orcamentoSegurado:
          dadosClinica.cabecalhoRodape?.orcamentoSegurado || false,
        planoTratamento: dadosClinica.cabecalhoRodape?.planoTratamento || false,
        atestado: dadosClinica.cabecalhoRodape?.atestado || false,
      });

      // Carregar imagens existentes se houver
      const imagensExistentes = [];
      if (dadosClinica.cabecalhoRodape?.cabecalho) {
        imagensExistentes.push({
          tipo: "cabecalho",
          urlExistente: dadosClinica.cabecalhoRodape.cabecalho,
          isExistente: true,
        });
      }
      if (dadosClinica.cabecalhoRodape?.rodape) {
        imagensExistentes.push({
          tipo: "rodape",
          urlExistente: dadosClinica.cabecalhoRodape.rodape,
          isExistente: true,
        });
      }
      setImagens(imagensExistentes);
    }
  }, [dadosClinica, form]);

  const handleSwitchChange = (field, value) => {
    setConfiguracao((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event, tipo) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    // Validações
    const maxSizeInBytes = 3 * 1024 * 1024; // 3MB
    const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];

    if (!allowedTypes.includes(selectedFile.type)) {
      setErrorMessage(`O arquivo deve ser uma imagem (PNG, JPG, JPEG).`);
      return;
    }

    if (selectedFile.size > maxSizeInBytes) {
      setErrorMessage(
        `O arquivo "${selectedFile.name}" excede o limite de 3MB.`,
      );
      return;
    }

    // Remover imagem existente do mesmo tipo se houver
    const imagensFiltradas = imagens.filter((img) => img.tipo !== tipo);

    // Adicionar nova imagem
    const novaImagem = {
      file: selectedFile,
      tipo: tipo,
      preview: URL.createObjectURL(selectedFile),
      isExistente: false,
    };

    setImagens([...imagensFiltradas, novaImagem]);
    setErrorMessage("");

    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = "";
  };

  const handleRemoveImage = (tipo) => {
    const imagensFiltradas = imagens.filter((img) => img.tipo !== tipo);
    setImagens(imagensFiltradas);
    setErrorMessage("");
  };

  const preSubmitHandler = () => {
    setErrorMessage("");
    setIsSubmitting(true);
    handleSubmit();
  };

  const handleSubmit = async () => {
    startLoading();

    try {
      const formData = new FormData();

      // Adicionar configurações de ativação
      formData.append("orcamentoParticular", configuracao.orcamentoParticular);
      formData.append("orcamentoSegurado", configuracao.orcamentoSegurado);
      formData.append("planoTratamento", configuracao.planoTratamento);
      formData.append("atestado", configuracao.atestado);
      formData.append("utilizador", auth.userId);

      // Adicionar imagens novas
      const imagensNovas = imagens.filter((img) => !img.isExistente);
      imagensNovas.forEach((img) => {
        formData.append("imagens", img.file);
        formData.append("tipos", img.tipo);
      });

      // Indicar quais imagens existentes devem ser mantidas
      const imagensExistentes = imagens.filter((img) => img.isExistente);
      imagensExistentes.forEach((img) => {
        formData.append("manterId", img.tipo);
      });

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clinicas/cabecalho-rodape/${clinicaId}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      handleUpdateCabecalhoRodape(response.clinica);
      toast.success("Configurações salvas com sucesso!");
      handleModalClose();
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast.error("Erro ao salvar configurações");
    } finally {
      stopLoading();
      setIsSubmitting(false);
    }
  };

  const getImagemPorTipo = (tipo) => {
    return imagens.find((img) => img.tipo === tipo);
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Image size={20} />
          <span>Configurações de Cabeçalho e Rodapé</span>
        </div>
      }
      open={openModal}
      onCancel={handleModalClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleModalClose}>
          Cancelar
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={preSubmitHandler}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting ? "Guardando..." : "Salvar Configurações"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Title level={4}>Ativar/Desativar por Documento</Title>

        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item name="orcamentoParticular">
              <Space>
                <Switch
                  checked={configuracao.orcamentoParticular}
                  onChange={(value) =>
                    handleSwitchChange("orcamentoParticular", value)
                  }
                />
                <Receipt size={16} />
                <Text>Orçamentos Particular</Text>
              </Space>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="orcamentoSegurado">
              <Space>
                <Switch
                  checked={configuracao.orcamentoSegurado}
                  onChange={(value) =>
                    handleSwitchChange("orcamentoSegurado", value)
                  }
                />
                <Receipt size={16} />
                <Text>Orçamentos Segurado</Text>
              </Space>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="planoTratamento">
              <Space>
                <Switch
                  checked={configuracao.planoTratamento}
                  onChange={(value) =>
                    handleSwitchChange("planoTratamento", value)
                  }
                />
                <FileText size={16} />
                <Text>Planos de Tratamento</Text>
              </Space>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="atestado">
              <Space>
                <Switch
                  checked={configuracao.atestado}
                  onChange={(value) => handleSwitchChange("atestado", value)}
                />
                <FileText size={16} />
                <Text>Atestados</Text>
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Title level={4} style={{ marginTop: 24 }}>
          Imagens
        </Title>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item label="Cabeçalho">
              {getImagemPorTipo("cabecalho") ? (
                <ElementoImagem
                  imagem={getImagemPorTipo("cabecalho")}
                  onRemove={() => handleRemoveImage("cabecalho")}
                />
              ) : (
                <div>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    style={{ display: "none" }}
                    id="cabecalhoInput"
                    onChange={(e) => handleFileChange(e, "cabecalho")}
                  />
                  <label
                    htmlFor="cabecalhoInput"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      border: "1px dashed #d9d9d9",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      width: "100%",
                      justifyContent: "center",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.borderColor = "#1890ff";
                      e.target.style.backgroundColor = "#f0f8ff";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.borderColor = "#d9d9d9";
                      e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    <UploadIcon size={16} />
                    Escolher Cabeçalho
                  </label>
                </div>
              )}
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Rodapé">
              {getImagemPorTipo("rodape") ? (
                <ElementoImagem
                  imagem={getImagemPorTipo("rodape")}
                  onRemove={() => handleRemoveImage("rodape")}
                />
              ) : (
                <div>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    style={{ display: "none" }}
                    id="rodapeInput"
                    onChange={(e) => handleFileChange(e, "rodape")}
                  />
                  <label
                    htmlFor="rodapeInput"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      border: "1px dashed #d9d9d9",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.3s",
                      width: "100%",
                      justifyContent: "center",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.borderColor = "#1890ff";
                      e.target.style.backgroundColor = "#f0f8ff";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.borderColor = "#d9d9d9";
                      e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    <UploadIcon size={16} />
                    Escolher Rodapé
                  </label>
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>

        {errorMessage && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: "#fff2f0",
              border: "1px solid #ffccc7",
              borderRadius: 6,
              color: "#ff4d4f",
            }}
          >
            {errorMessage}
          </div>
        )}

        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: "#f6f6f6",
            borderRadius: 6,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            • Formatos aceitos: PNG, JPG, JPEG
            <br />
            • Tamanho máximo: 3MB por imagem
            <br />
            • Recomendação: Use imagens com fundo transparente para melhor
            resultado
            <br />• Dimensões recomendadas: Cabeçalho (1200x200px), Rodapé
            (1200x150px)
          </Text>
        </div>
      </Form>
    </Modal>
  );
}

export default ModalConfiguracoesCabecalhoRodape;
